import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { CalendarCheck, Phone, Plus, Trash, Clock } from "@phosphor-icons/react";

const DAYS = [
  ["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"],
  ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"],
];
const STATUS_STYLE = {
  confirmed: "bg-emerald-500/10 text-emerald-600",
  cancelled: "bg-red-500/10 text-red-600",
  completed: "bg-blue-500/10 text-blue-600",
  no_show: "bg-amber-500/10 text-amber-600",
};

export default function Appointments() {
  const qc = useQueryClient();
  const [cfg, setCfg] = useState(null);

  const { data } = useQuery({
    queryKey: ["appt-config"],
    queryFn: async () => (await api.get("/appointments/config")).data,
    onSuccess: (d) => { if (!cfg) setCfg(d.config); },
  });
  const { data: mine } = useQuery({
    queryKey: ["appt-mine"],
    queryFn: async () => (await api.get("/appointments/mine?upcoming=false")).data,
  });

  const c = cfg || data?.config;
  const set = (patch) => setCfg({ ...(c || {}), ...patch });

  const save = useMutation({
    mutationFn: async () => (await api.post("/appointments/config", {
      business_name: c.business_name, services: c.services, hours: c.hours,
      slot_min: Number(c.slot_min) || 30, timezone: c.timezone, enabled: c.enabled,
      booking_did: c.booking_did, answer_all_calls: c.answer_all_calls,
    })).data,
    onSuccess: () => { toast.success("Appointment settings saved"); qc.invalidateQueries({ queryKey: ["appt-config"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save"),
  });

  const setStatus = useMutation({
    mutationFn: async ({ id, status }) => (await api.post(`/appointments/${id}/status`, { status })).data,
    onSuccess: () => { toast.success("Updated"); qc.invalidateQueries({ queryKey: ["appt-mine"] }); },
    onError: () => toast.error("Could not update"),
  });

  const { data: sub } = useQuery({
    queryKey: ["appt-sub"],
    queryFn: async () => (await api.get("/appointments/subscription")).data,
  });
  const entitled = sub?.entitled;
  const subscribe = useMutation({
    mutationFn: async (cycle) => (await api.post(`/appointments/subscribe?cycle=${cycle}`)).data,
    onSuccess: () => { toast.success("SI Appointment Scheduling activated"); qc.invalidateQueries({ queryKey: ["appt-sub"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not subscribe"),
  });

  if (!c) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const services = c.services || [];
  const hours = c.hours || {};
  const setHour = (day, key, val) => set({ hours: { ...hours, [day]: { ...(hours[day] || {}), [key]: val } } });
  const setService = (i, key, val) => {
    const next = services.map((s, idx) => (idx === i ? { ...s, [key]: val } : s));
    set({ services: next });
  };

  return (
    <div className="mx-auto max-w-4xl space-y-8" data-testid="appointments-page">
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"><CalendarCheck size={26} weight="duotone" /></div>
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">SI Appointment Booking</h1>
          <p className="mt-1 text-muted-foreground">Your SI receptionist answers the phone and books appointments 24/7 — even after hours.</p>
        </div>
      </div>

      {sub && !entitled && (
        <section className="rounded-3xl border border-amber-500/40 bg-amber-500/5 p-6" data-testid="appt-paywall">
          <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-amber-500"><CalendarCheck size={15} weight="fill" /> Paid add-on</p>
          <h2 className="mt-2 text-xl font-semibold">Activate SI Appointment Scheduling</h2>
          <p className="mt-1 text-sm text-muted-foreground">Add a 24/7 SI receptionist to your business line that answers calls and books appointments automatically. Subscribe to switch it on.</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button data-testid="appt-subscribe-monthly" onClick={() => subscribe.mutate("monthly")} disabled={subscribe.isPending}
              className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105 disabled:opacity-60">
              Subscribe — ${(((sub.prices || {}).monthly || 0) / 100).toFixed(2)}/mo
            </button>
            <button data-testid="appt-subscribe-annual" onClick={() => subscribe.mutate("annual")} disabled={subscribe.isPending}
              className="rounded-full border border-border px-5 py-2.5 text-sm font-semibold transition-colors hover:border-primary disabled:opacity-60">
              Annual — ${(((sub.prices || {}).annual || 0) / 100).toFixed(2)}/yr
            </button>
          </div>
        </section>
      )}

      {/* Front door: SI answers every call */}
      <section className="rounded-3xl border border-primary/40 bg-primary/5 p-6" data-testid="appt-frontdoor-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="max-w-xl">
            <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary"><Phone size={15} weight="fill" /> Let the SI answer my phone</p>
            <p className="mt-2 text-sm text-muted-foreground">Turn this on and <b>every call to your iYeneh number is answered by the full-duplex SI receptionist</b> that books appointments — 24/7, no extra setup needed. Turn it off to keep your normal call flow (and only reach the SI via a menu key).</p>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" data-testid="appt-answer-all" checked={!!c.answer_all_calls} disabled={!entitled} onChange={(e) => set({ answer_all_calls: e.target.checked })} className="h-5 w-5 accent-[var(--primary)] disabled:opacity-40" />
            {!entitled ? "Subscribe to enable" : (c.answer_all_calls ? "SI answering ON" : "Off")}
          </label>
        </div>
        <button data-testid="appt-save-frontdoor" onClick={() => save.mutate()} disabled={save.isPending}
          className="mt-4 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105 disabled:opacity-60">
          {save.isPending ? "Saving…" : "Save"}
        </button>
      </section>

      {/* Booking line + status */}
      <section className="rounded-3xl border border-border bg-card p-6" data-testid="appt-line-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary"><Phone size={15} weight="fill" /> SI booking line</p>
            <p className="mt-2 text-sm text-muted-foreground">Enter the phone number customers call to book. Point that number's voice webhook (in your provider) to the URL below.</p>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" data-testid="appt-enabled" checked={!!c.enabled} onChange={(e) => set({ enabled: e.target.checked })} className="h-4 w-4 accent-[var(--primary)]" />
            Booking enabled
          </label>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Booking number (E.164)</label>
            <input data-testid="appt-booking-did" value={c.booking_did || ""} onChange={(e) => set({ booking_did: e.target.value })}
              placeholder="+12025551234" className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Voice webhook URL (iYeneh sets this for you)</label>
            <input data-testid="appt-webhook" readOnly value={data?.voice_webhook_url || ""} onFocus={(e) => e.target.select()}
              className="mt-1 w-full rounded-xl border border-border bg-secondary px-3 py-2 text-xs font-mono" />
          </div>
        </div>
      </section>

      {/* Business + slot settings */}
      <section className="rounded-3xl border border-border bg-card p-6 space-y-4" data-testid="appt-settings-card">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-muted-foreground">Business name (what the SI says)</label>
            <input data-testid="appt-business-name" value={c.business_name || ""} onChange={(e) => set({ business_name: e.target.value })}
              placeholder="e.g. Bright Smile Dental" className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Slot length (min)</label>
            <input data-testid="appt-slot-min" type="number" min="5" step="5" value={c.slot_min || 30} onChange={(e) => set({ slot_min: e.target.value })}
              className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Timezone</label>
          <input data-testid="appt-timezone" value={c.timezone || ""} onChange={(e) => set({ timezone: e.target.value })}
            placeholder="America/New_York" className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </div>

        {/* Hours */}
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold"><Clock size={16} weight="duotone" /> Opening hours</p>
          <div className="mt-2 space-y-2">
            {DAYS.map(([d, label]) => {
              const h = hours[d] || {};
              return (
                <div key={d} className="flex flex-wrap items-center gap-3 rounded-xl border border-border/60 bg-background px-3 py-2" data-testid={`appt-hours-${d}`}>
                  <span className="w-24 text-sm font-medium">{label}</span>
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <input type="checkbox" checked={!!h.closed} onChange={(e) => setHour(d, "closed", e.target.checked)} className="h-4 w-4 accent-[var(--primary)]" />
                    Closed
                  </label>
                  {!h.closed && (
                    <div className="flex items-center gap-2">
                      <input type="time" value={h.open || "09:00"} onChange={(e) => setHour(d, "open", e.target.value)}
                        className="rounded-lg border border-border bg-background px-2 py-1 text-sm" />
                      <span className="text-muted-foreground">—</span>
                      <input type="time" value={h.close || "17:00"} onChange={(e) => setHour(d, "close", e.target.value)}
                        className="rounded-lg border border-border bg-background px-2 py-1 text-sm" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Services */}
        <div>
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">Services</p>
            <button data-testid="appt-add-service" onClick={() => set({ services: [...services, { name: "", duration_min: c.slot_min || 30 }] })}
              className="flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold hover:bg-secondary/70"><Plus size={13} weight="bold" /> Add service</button>
          </div>
          <div className="mt-2 space-y-2">
            {services.length === 0 && <p className="text-xs text-muted-foreground">No services yet — the SI will offer a general appointment.</p>}
            {services.map((s, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`appt-service-${i}`}>
                <input value={s.name || ""} onChange={(e) => setService(i, "name", e.target.value)} placeholder="Service name"
                  className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm" />
                <input type="number" min="5" step="5" value={s.duration_min || 30} onChange={(e) => setService(i, "duration_min", Number(e.target.value))}
                  className="w-24 rounded-xl border border-border bg-background px-3 py-2 text-sm" title="Minutes" />
                <button data-testid={`appt-remove-service-${i}`} onClick={() => set({ services: services.filter((_, idx) => idx !== i) })}
                  className="rounded-lg p-2 text-muted-foreground hover:bg-red-500/10 hover:text-red-600"><Trash size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        <button data-testid="appt-save" onClick={() => save.mutate()} disabled={save.isPending}
          className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105 disabled:opacity-60">
          {save.isPending ? "Saving…" : "Save settings"}
        </button>
      </section>

      {/* Bookings */}
      <section className="rounded-3xl border border-border bg-card p-6" data-testid="appt-bookings-card">
        <p className="text-sm font-semibold">Appointments</p>
        <div className="mt-3 space-y-2">
          {(mine?.appointments || []).length === 0 && <p className="text-sm text-muted-foreground">No appointments yet. When a customer calls your SI line and books, it appears here.</p>}
          {(mine?.appointments || []).map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/60 bg-background px-4 py-3" data-testid={`appt-booking-${a.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold">{a.customer_name} <span className="font-normal text-muted-foreground">· {a.service || "Appointment"}</span></p>
                <p className="text-xs text-muted-foreground">{a.date} at {a.time} · {a.customer_phone || "no phone"} {a.source === "ai_voice" && "· 🎙️ booked by SI"}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_STYLE[a.status] || "bg-secondary text-muted-foreground"}`}>{a.status}</span>
                {a.status === "confirmed" && (
                  <>
                    <button data-testid={`appt-complete-${a.id}`} onClick={() => setStatus.mutate({ id: a.id, status: "completed" })} className="rounded-lg px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-500/10">Complete</button>
                    <button data-testid={`appt-cancel-${a.id}`} onClick={() => setStatus.mutate({ id: a.id, status: "cancelled" })} className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-500/10">Cancel</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
